
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import { CuisineType, FoodType } from "@/types/recipe";

// Creator type options for filtering
const CREATOR_TYPES = [
  "BBQ Lovers",
  "Budget Cooks",
  "Chefs",
  "Health Enthusiasts",
  "Home Cooks",
  "Influencers",
  "Nutritionists",
  "Parents",
  "Students",
  "Vegetarians/Vegans",
];

// Nationality options for filtering
const NATIONALITIES = [
  "American",
  "Argentinian",
  "Australian",
  "Austrian",
  "Belgian",
  "Bosnian and Herzegovinian",
  "British",
  "Bulgarian",
  "Croatian",
  "Cypriot",
  "Czech",
  "Danish",
  "Dutch",
  "Estonian",
  "Finnish",
  "French",
  "German",
  "Greek",
  "Hungarian",
  "Icelandic",
  "Irish",
  "Italian",
  "Japanese",
  "Latvian",
  "Lithuanian",
  "Maltese",
  "Mexican",
  "Moldovan",
  "Montenegrin",
  "Norwegian",
  "Polish",
  "Portuguese",
  "Romanian",
  "Russian",
  "Serbian",
  "Slovak",
  "Slovenian",
  "South African",
  "South Korean",
  "Spanish",
  "Swedish",
  "Swiss",
  "Turkish",
  "Ukrainian",
  "Other"
];

// Cuisine type options
const CUISINE_TYPES: CuisineType[] = [
  'Italian', 'Lebanese', 'Greek', 'Indian', 'Chinese', 'Japanese',
  'Thai', 'Mexican', 'French', 'Spanish', 'American', 'Mediterranean',
  'Middle Eastern', 'Korean', 'Vietnamese', 'Turkish', 'Brazilian',
  'Caribbean', 'African', 'British', 'Other'
];

// Food type options
const FOOD_TYPES: FoodType[] = [
  'Chicken', 'Dessert', 'Snacks', 'Vegetables',
  'Ground Beef', 'Seafood', 'Soup', 'Pork', 'Other'
];

interface LibraryFiltersProps {
  search: string;
  setSearch: (value: string) => void;
  selectedCreatorType: string | null;
  setSelectedCreatorType: (value: string | null) => void;
  selectedNationality: string | null;
  setSelectedNationality: (value: string | null) => void;
  selectedCuisineType: CuisineType | null;
  setSelectedCuisineType: (value: CuisineType | null) => void;
  selectedFoodType: FoodType | null;
  setSelectedFoodType: (value: FoodType | null) => void;
  viewMode: "recipes" | "creators";
  availableFilters: {
    creatorTypes: string[];
    nationalities: string[];
    cuisineTypes: CuisineType[];
    foodTypes: FoodType[];
  };
}

export const LibraryFilters = ({
  search,
  setSearch,
  selectedCreatorType,
  setSelectedCreatorType,
  selectedNationality,
  setSelectedNationality,
  selectedCuisineType,
  setSelectedCuisineType,
  selectedFoodType,
  setSelectedFoodType,
  viewMode,
  availableFilters,
}: LibraryFiltersProps) => {
  const [expandedCreatorTypes, setExpandedCreatorTypes] = useState(true);
  const [expandedNationalities, setExpandedNationalities] = useState(false);
  const [expandedCuisineTypes, setExpandedCuisineTypes] = useState(false);
  const [expandedFoodTypes, setExpandedFoodTypes] = useState(false);

  const renderFilterGroup = (
    title: string,
    items: string[],
    availableItems: string[],
    selectedItem: string | null,
    setSelectedItem: (value: string | null) => void,
    isExpanded: boolean,
    setIsExpanded: (value: boolean) => void
  ) => {
    // Only show filter group if there are available items
    if (availableItems.length === 0) return null;

    // Filter items to only show available ones
    const filteredItems = items.filter(item => availableItems.includes(item));
    
    if (filteredItems.length === 0) return null;

    return (
      <div className="space-y-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between font-semibold text-lg hover:text-primary transition-colors"
        >
          <span>{title}</span>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
        
        {isExpanded && (
          <div className="space-y-2">
            {filteredItems.map((item) => (
              <button
                key={item}
                onClick={() => setSelectedItem(item === selectedItem ? null : item)}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                  item === selectedItem
                    ? "bg-primary text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="w-full md:w-64 space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          type="search"
          placeholder={viewMode === "recipes" ? "Search recipes..." : "Search creators..."}
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {renderFilterGroup(
        "Creator Type",
        CREATOR_TYPES,
        availableFilters.creatorTypes,
        selectedCreatorType,
        setSelectedCreatorType,
        expandedCreatorTypes,
        setExpandedCreatorTypes
      )}

      {renderFilterGroup(
        "Creator's Nationality",
        NATIONALITIES,
        availableFilters.nationalities,
        selectedNationality,
        setSelectedNationality,
        expandedNationalities,
        setExpandedNationalities
      )}

      {renderFilterGroup(
        "Cuisine Type",
        CUISINE_TYPES,
        availableFilters.cuisineTypes,
        selectedCuisineType,
        setSelectedCuisineType as (value: string | null) => void,
        expandedCuisineTypes,
        setExpandedCuisineTypes
      )}

      {renderFilterGroup(
        "Food Type",
        FOOD_TYPES,
        availableFilters.foodTypes,
        selectedFoodType,
        setSelectedFoodType as (value: string | null) => void,
        expandedFoodTypes,
        setExpandedFoodTypes
      )}
    </aside>
  );
};
